import { useEffect, useState } from "react";
import { KYDScope } from "@common/interfaces/ICognito";
import { Badge, Box, Checkbox, HStack, Text, VStack } from "@chakra-ui/react";

interface PermissionNode {
  name: string;
  children?: PermissionNode[];
  scopes: {
    read?: KYDScope;
    write?: KYDScope;
  };
}

export const LABELS: Partial<
  Record<
    | KYDScope
    | "events"
    | "promo_codes"
    | "access_codes"
    | "ticket_types"
    | "marketing"
    | "metadata"
    | "attendees"
    | "waitlist"
    | "guestlist"
    | "analytics"
    | "organizations"
    | "bundles"
    | "users"
    | "bio"
    | "affiliates"
    | "boxoffice"
    | "payouts"
    | "overview"
    | "reports",
    string
  >
> = {
  "events/write": "Update all your event's information",
  "events/read": "View all of your event's information",
  "events/promo_codes:read": "View promocodes.",
  "events/promo_codes:write": "Update promocodes",
  "events/access_codes:read": "View access codes",
  "events/access_codes:write": "Update access codes",
  "events/ticket_types:read": "View ticket types",
  "events/ticket_types:write": "Manage ticket types",
  "events/marketing:read": "View marketing",
  "events/marketing:write": "Update marketing",
  // "events/campaigns:read": "View campaigns",
  // "events/campaigns:write": "Update campaigns",
  "events/metadata:read": "View event metadata",
  "events/metadata:write": "Update event metadata",
  "events/guestlist:read": "View guest list",
  "events/guestlist:write": "Manage guest list",
  "events/attendees:read": "View attendees",
  "events/attendees:write": "Manage attendees",
  "events/waitlist:read": "View waitlist",
  "events/waitlist:write": "Manage waitlist and fulfill orders",
  "events/analytics:read": "View event analytics",
  "events/affiliates:read": "View event affiliates",
  "events/affiliates:write": "Manage event affiliates",
  "events/boxoffice:read": "View event data in box office app",
  "events/boxoffice:write":
    "Check in patrons and collect payments in box office app",
  "events/reports:read": "View event reports",
  "events/reports:write": "Manage who receives event reports",
  "organizations/read": "View all access to your organization",
  "organizations/write":
    "Change anything about your organization, including team, events, bundles, bio, etc.",
  "organizations/bundles:read": "View bundles",
  "organizations/bundles:write": "Manage bundles",
  "organizations/users:read": "View team",
  "organizations/users:write": "Manage team",
  "organizations/events:read": "View all events",
  "organizations/events:write": "Create new events",
  "organizations/bio:read": "View bio",
  "organizations/bio:write": "Manage bio",
  "organizations/payouts:read": "View payouts related data",
  "organizations/payouts:write": "Update payouts related data",
  "organizations/overview:read": "View organization overview",
  "organizations/overview:write": "Update organization overview",
  promo_codes: "Promo Codes",
  ticket_types: "Ticket Types",
  marketing: "Marketing",
  metadata: "Metadata",
  guestlist: "Guestlist",
  events: "Events",
  access_codes: "Access Codes",
  attendees: "Attendees",
  analytics: "Analytics",
  affiliates: "Affiliates",
  organizations: "Organization",
  bundles: "Bundles",
  users: "Team",
  bio: "Bio",
  waitlist: "Waitlist",
  boxoffice: "Box Office",
  payouts: "Payouts",
  overview: "Overview",
  reports: "Reports",
};

const organizationPermissionStructure: PermissionNode[] = [
  {
    name: "organizations",
    scopes: { read: "organizations/read", write: "organizations/write" },
    children: [
      {
        name: "events",
        scopes: {
          read: "organizations/events:read",
          write: "organizations/events:write",
        },
      },
      {
        name: "overview",
        scopes: {
          read: "organizations/overview:read",
          write: "organizations/overview:write",
        },
      },
      {
        name: "payouts",
        scopes: {
          read: "organizations/payouts:read",
          write: "organizations/payouts:write",
        },
      },
      {
        name: "users",
        scopes: {
          read: "organizations/users:read",
          write: "organizations/users:write",
        },
      },
      {
        name: "bundles",
        scopes: {
          read: "organizations/bundles:read",
          write: "organizations/bundles:write",
        },
      },
      {
        name: "bio",
        scopes: {
          read: "organizations/bio:read",
          write: "organizations/bio:write",
        },
      },
    ],
  },
];

const eventPermissionStructure: PermissionNode[] = [
  {
    name: "events",
    scopes: { read: "events/read", write: "events/write" },
    children: [
      {
        name: "metadata",
        scopes: {
          read: "events/metadata:read",
          write: "events/metadata:write",
        },
      },
      {
        name: "ticket_types",
        scopes: {
          read: "events/ticket_types:read",
          write: "events/ticket_types:write",
        },
      },
      {
        name: "promo_codes",
        scopes: {
          read: "events/promo_codes:read",
          write: "events/promo_codes:write",
        },
      },
      {
        name: "access_codes",
        scopes: {
          read: "events/access_codes:read",
          write: "events/access_codes:write",
        },
      },
      {
        name: "marketing",
        scopes: {
          read: "events/marketing:read",
          write: "events/marketing:write",
        },
      },
      // TODO: uncomment to launch campaigns
      // {
      //   name: "campaigns",
      //   scopes: {
      //     read: "events/campaigns:read",
      //     write: "events/campaigns:write",
      //   },
      // },
      {
        name: "guestlist",
        scopes: {
          read: "events/guestlist:read",
          write: "events/guestlist:write",
        },
      },
      {
        name: "attendees",
        scopes: {
          read: "events/attendees:read",
          write: "events/attendees:write",
        },
      },
      {
        name: "waitlist",
        scopes: {
          read: "events/waitlist:read",
          write: "events/waitlist:write",
        },
      },
      {
        name: "affiliates",
        scopes: {
          read: "events/affiliates:read",
          write: "events/affiliates:write",
        },
      },
      {
        name: "analytics",
        scopes: {
          read: "events/analytics:read",
        },
      },
      {
        name: "reports",
        scopes: {
          read: "events/reports:read",
          write: "events/reports:write",
        },
      },
      {
        name: "boxoffice",
        scopes: {
          read: "events/boxoffice:read",
          write: "events/boxoffice:write",
        },
      },
    ],
  },
];

const getAllChildScopes = (node: PermissionNode): KYDScope[] => {
  let scopes: KYDScope[] = [node.scopes.read, node.scopes.write].filter(
    Boolean
  );
  if (node.children) {
    node.children.forEach((child) => {
      scopes = scopes.concat(getAllChildScopes(child));
    });
  }
  return scopes;
};

const PermissionCheckbox = ({
  node,
  isDisabled = false,
  selectedScopes,
  onToggle,
  level,
  parentReadChecked = false,
  parentWriteChecked = false,
}: {
  node: PermissionNode;
  isDisabled?: boolean;
  selectedScopes: Set<KYDScope>;
  onToggle: (scopes: KYDScope[], isChecked: boolean) => void;
  level: number;
  parentReadChecked?: boolean;
  parentWriteChecked?: boolean;
}) => {
  const isReadChecked =
    selectedScopes.has(node.scopes.read) || parentReadChecked;

  const isWriteChecked =
    selectedScopes.has(node.scopes.write) || parentWriteChecked;

  const handleReadToggle = () => {
    const scopesToToggle = [
      node.scopes.read,
      ...getAllChildScopes(node).filter((scope) => scope.endsWith(":read")),
    ];
    onToggle(scopesToToggle, !isReadChecked);
  };

  const handleWriteToggle = () => {
    const scopesToToggle = [
      node.scopes.read,
      node.scopes.write,
      ...getAllChildScopes(node).filter((scope) => scope.endsWith(":write")),
    ];
    onToggle(scopesToToggle, !isWriteChecked);
  };

  return (
    <VStack
      my={2}
      w="full"
      borderWidth={level === 0 ? 1 : 0}
      borderColor={"gray.200"}
      p={2}
      spacing={1}
      borderRadius={8}
    >
      <Text w="full" fontWeight={"bold"}>
        {LABELS[node.name] || node.name}
      </Text>
      {node.scopes.read ? (
        <Checkbox
          w="full"
          defaultChecked={isReadChecked}
          isChecked={isReadChecked || node.name === "metadata"}
          isDisabled={
            parentReadChecked || isDisabled || node.name === "metadata"
          }
          disabled={parentReadChecked || isDisabled || node.name === "metadata"}
          onChange={handleReadToggle}
        >
          <VStack w="full" spacing={0}>
            <HStack w="full" justifyContent={"space-between"}>
              <Text>{LABELS[node.scopes.read]} </Text>
              <Badge
                justifyItems={"center"}
                maxW={"100px"}
                size={"xl"}
                colorScheme={"green"}
              >
                READ
              </Badge>
            </HStack>
            <Text w="full" fontSize={"xs"}></Text>
          </VStack>
        </Checkbox>
      ) : null}
      {node.scopes.write ? (
        <Checkbox
          w="full"
          defaultChecked={isWriteChecked}
          isChecked={isWriteChecked}
          onChange={handleWriteToggle}
          disabled={parentWriteChecked || isDisabled}
          isDisabled={parentWriteChecked || isDisabled}
        >
          <VStack w="full" spacing={0}>
            <HStack w="full" justifyContent={"space-between"}>
              <Text>{LABELS[node.scopes.write]} </Text>
              <Badge
                justifyItems={"center"}
                maxW={"100px"}
                size={"xl"}
                colorScheme={"red"}
              >
                WRITE
              </Badge>
            </HStack>
          </VStack>
        </Checkbox>
      ) : null}
      <Box pl={4} w="full">
        {node.children?.map((child, index) => (
          <PermissionCheckbox
            isDisabled={isDisabled}
            key={index}
            node={child}
            selectedScopes={selectedScopes}
            onToggle={onToggle}
            level={level + 1}
            parentReadChecked={isReadChecked}
            parentWriteChecked={isWriteChecked}
          />
        ))}
      </Box>
    </VStack>
  );
};

const PermissionSelector = ({
  mode,
  scopes,
  isDisabled = false,
  onChange = () => {},
}: {
  mode: "team" | "organization";
  scopes: KYDScope[];
  isDisabled?: boolean;
  onChange: (values: { scopes: KYDScope[] }) => void;
}) => {
  const [selectedScopes, setSelectedScopes] = useState<Set<KYDScope>>(
    new Set(scopes)
  );

  const permissions =
    mode === "organization"
      ? eventPermissionStructure
      : organizationPermissionStructure.concat(eventPermissionStructure);

  useEffect(() => {
    setSelectedScopes(new Set(scopes));
  }, [scopes]);

  const handleToggle = (scopes: KYDScope[], isChecked: boolean) => {
    setSelectedScopes((prevScopes) => {
      const newScopes = new Set(prevScopes);
      scopes.forEach((scope) => {
        if (isChecked) {
          newScopes.add(scope);
        } else {
          newScopes.delete(scope);
        }
      });
      onChange({ scopes: Array.from(newScopes) });

      return newScopes;
    });
  };

  const isNodeDisabled = (node: PermissionNode) => {
    if (node.name === "events" && mode === "team") {
      return !(
        selectedScopes.has("organizations/events:read") ||
        selectedScopes.has("organizations/events:write")
      );
    }

    return false;
  };

  return (
    <VStack w="full" overflowY={"scroll"} h="500px">
      {mode === "organization" ? (
        <>
          <Text fontWeight={"medium"} w="full">
            Organization has access to:
          </Text>
          <Text w="full">
            By default an invited organization will be able to see a summary of
            your event including: name, description, timeline, venue,
            performers, genres.
          </Text>
        </>
      ) : null}
      {permissions.map((node, index) => (
        <PermissionCheckbox
          isDisabled={isNodeDisabled(node)}
          key={index}
          node={node}
          selectedScopes={selectedScopes}
          onToggle={handleToggle}
          level={0}
        />
      ))}
    </VStack>
  );
};

export default PermissionSelector;
